/**
 * Spatial focus for a remote control.
 *
 * A television has no pointer. Every interactive element must be reachable by pressing
 * an arrow, and "reachable" has to mean what a person sitting on a sofa expects: the
 * thing that looks like it is to the right.
 *
 * ## Why geometry rather than DOM order
 *
 * Tab order is a single sequence; a TV layout is a plane. A rail of twenty cards above a
 * row of three buttons has no linear order that makes Down do the right thing from every
 * card. So candidates are scored from their actual on-screen rectangles, which is also
 * the only approach that survives a layout change without anyone updating a list.
 *
 * ## The scoring rule
 *
 * Lives in `geometry.ts`, as pure functions over rectangles, so it can be tested against
 * a synthetic layout instead of through a DOM that reports every rectangle as zero. This
 * file owns the registry, the focus state and the key handling; that one owns the rule.
 *
 * ## No dependency
 *
 * `@noriginmedia/norigin-spatial-navigation` does this well and is the fallback if this
 * turns out to need features it already has. It is ~200 lines of behaviour we need
 * exactly, against a library that would also need its own focus-restoration and
 * scroll-into-view conventions learned. Written out, it is auditable.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { DIRECTION_BY_KEY, KEY, registerMediaKeys } from '../keys';
import { bestCandidate, rectOf, type Direction } from './geometry';

export interface FocusableEntry {
  id: string;
  /** Grouping label, used to restore focus to the rail you left rather than its first card. */
  group?: string;
  element: HTMLElement;
  onSelect?: () => void;
  /** Skipped by navigation while true — a disabled button, or a card still loading. */
  disabled?: boolean;
}

interface FocusApi {
  focusedId: string | null;
  register: (entry: FocusableEntry) => () => void;
  focus: (id: string) => void;
  /** Moves focus one step. Returns false when there was nothing in that direction. */
  move: (direction: Direction) => boolean;
  /** Activates the focused element. */
  select: () => void;
  /** The element focus is on right now, read between renders. */
  focusedElement: () => HTMLElement | null;
  /** Remembers, then restores, the focused id for a screen. */
  saveGroupFocus: (group: string, id: string) => void;
  restoreGroupFocus: (group: string) => boolean;
}

const FocusCtx = createContext<FocusApi | null>(null);

export function FocusProvider({ children }: { children: ReactNode }) {
  const entries = useRef(new Map<string, FocusableEntry>());
  const groupMemory = useRef(new Map<string, string>());
  const [focusedId, setFocusedId] = useState<string | null>(null);
  // The ref, not the state, is the source of truth for "what is focused right now".
  // State drives rendering and lags by a commit; registration and key handling both run
  // between commits and need the current answer. Every writer below updates both.
  const focusedRef = useRef<string | null>(null);

  const focus = useCallback((id: string) => {
    const entry = entries.current.get(id);
    if (!entry || entry.disabled) return;

    // The ref is updated *here*, not only on the next render, and that is load-bearing.
    // `register` focuses the first element to appear when nothing is focused yet, and a
    // screen registers all of its elements in one synchronous pass. With the ref written
    // only during render, `setFocusedId` had not taken effect yet, so every element in
    // that pass still saw "nothing is focused" and grabbed it — leaving focus on the
    // *last* one registered. On the login screen that put the ring on "Kayıt olun"
    // instead of the email field.
    focusedRef.current = id;
    setFocusedId(id);
    if (entry.group) groupMemory.current.set(entry.group, id);

    // `nearest` on both axes: a rail should slide the minimum needed, not centre every
    // card. Centring makes a horizontal rail lurch on every press.
    entry.element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    // The DOM focus follows the logical focus so screen readers and the platform agree
    // with what is drawn.
    entry.element.focus({ preventScroll: true });
  }, []);

  const register = useCallback(
    (entry: FocusableEntry) => {
      entries.current.set(entry.id, entry);
      // First focusable to appear takes focus, so a screen is never rendered with
      // nothing selected and no way to select anything.
      if (focusedRef.current === null && !entry.disabled) focus(entry.id);

      return () => {
        entries.current.delete(entry.id);
        if (focusedRef.current !== entry.id) return;
        // The focused element went away — a card scrolled out of a windowed rail, or a
        // dialog closed. Falling back to nothing would strand the remote, so pick the
        // nearest surviving candidate instead.
        const next = entries.current.values().next().value;
        if (next) {
          focus(next.id);
        } else {
          focusedRef.current = null;
          setFocusedId(null);
        }
      };
    },
    [focus],
  );

  const move = useCallback(
    (direction: Direction): boolean => {
      const currentId = focusedRef.current;
      const current = currentId ? entries.current.get(currentId) : null;
      if (!current) {
        const first = entries.current.values().next().value;
        if (first) focus(first.id);
        return Boolean(first);
      }

      const from = rectOf(current.element);
      const candidates: { id: string; rect: ReturnType<typeof rectOf> }[] = [];

      for (const entry of entries.current.values()) {
        if (entry.id === currentId || entry.disabled) continue;
        // A detached or hidden element has a zero rect, which would score as adjacent to
        // everything.
        if (!entry.element.isConnected || entry.element.offsetParent === null) continue;
        candidates.push({ id: entry.id, rect: rectOf(entry.element) });
      }

      // The viewport is passed so a card scrolled far out of a rail is not a candidate —
      // see `nearViewport` for the defect that motivates it.
      const best = bestCandidate(from, candidates, direction, {
        width: window.innerWidth,
        height: window.innerHeight,
      });
      if (!best) return false;
      focus(best.id);
      return true;
    },
    [focus],
  );

  const focusedElement = useCallback((): HTMLElement | null => {
    const id = focusedRef.current;
    return id ? (entries.current.get(id)?.element ?? null) : null;
  }, []);

  const select = useCallback(() => {
    const id = focusedRef.current;
    if (!id) return;
    entries.current.get(id)?.onSelect?.();
  }, []);

  const saveGroupFocus = useCallback((group: string, id: string) => {
    groupMemory.current.set(group, id);
  }, []);

  const restoreGroupFocus = useCallback(
    (group: string): boolean => {
      const id = groupMemory.current.get(group);
      if (!id || !entries.current.has(id)) return false;
      focus(id);
      return true;
    },
    [focus],
  );

  // Media keys need asking for; arrows, Enter and Back do not.
  useEffect(() => {
    registerMediaKeys();
  }, []);

  const api = useMemo<FocusApi>(
    () => ({
      focusedId,
      register,
      focus,
      move,
      select,
      focusedElement,
      saveGroupFocus,
      restoreGroupFocus,
    }),
    [focusedId, register, focus, move, select, focusedElement, saveGroupFocus, restoreGroupFocus],
  );

  return <FocusCtx.Provider value={api}>{children}</FocusCtx.Provider>;
}

export function useFocus(): FocusApi {
  const ctx = useContext(FocusCtx);
  if (!ctx) throw new Error('useFocus must be used inside a FocusProvider');
  return ctx;
}

/**
 * Drives navigation from the arrow keys.
 *
 * Mounted once, at the top of the TV layout. `preventDefault` is unconditional on every
 * handled key: on a TV an unhandled arrow scrolls the page under the focus ring, and an
 * unhandled Back closes the app.
 */
export function useSpatialNavigation(onBack: () => void): void {
  const { move, select } = useFocus();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const direction = DIRECTION_BY_KEY[e.keyCode];
      if (direction) {
        e.preventDefault();
        move(direction);
        return;
      }
      if (e.keyCode === KEY.ENTER) {
        e.preventDefault();
        select();
        return;
      }
      if (e.keyCode === KEY.BACK) {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [move, select, onBack]);
}
